import React, { useCallback, useContext, useEffect } from "react";
import { useState } from "react";
import {
  Navbar,
  createStyles,
  Modal,
  Button,
  UnstyledButton,
} from "@mantine/core";
import {
  IconLogout,
  IconSwitchHorizontal,
  IconReceipt,
  IconCamera,
  IconUserEdit,
  IconLock,
  IconTicket,
  IconUserPlus,
  IconRefresh,
  IconHome,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import ActivePerson from "./activePerson";
import { AccountContext } from "../accountContext";
import Person from "./person";
import { Link } from "@reach/router";
import { getLinks } from "../util";
import LogoutModal from "./logoutModal";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";

const useStyles = createStyles((theme) => ({
  root: {
    zIndex: 200,
    borderColor:
      theme.colorScheme === "dark" || theme.colorScheme === "darkest"
        ? theme.colors.dark[7]
        : "#e9ecef",
  },

  navbar: {
    height: "inherit",
    backgroundColor:
      theme.colorScheme === "dark"
        ? "rgb(48, 48, 48)"
        : theme.colorScheme === "darkest"
        ? "rgb(12, 18, 23)"
        : theme.white,
  },

  link: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: theme.fontSizes.sm,
    color:
      theme.colorScheme === "dark" || theme.colorScheme === "darkest"
        ? theme.colors.dark[1]
        : theme.colors.gray[7],
    padding: `0.5rem 0.5rem`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,
    width: "100%",
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark" || theme.colorScheme === "darkest"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
      color:
        theme.colorScheme === "dark" || theme.colorScheme === "darkest"
          ? theme.white
          : theme.black,

      [`& .${"icon"}`]: {
        color:
          theme.colorScheme === "dark" || theme.colorScheme === "darkest"
            ? theme.white
            : theme.black,
      },
    },
  },

  linkIcon: {
    ref: "icon",
    color:
      theme.colorScheme === "dark" || theme.colorScheme === "darkest"
        ? theme.colors.dark[2]
        : theme.colors.gray[6],
    marginRight: theme.spacing.sm,
  },

  linkActive: {
    "&, &:hover": {
      backgroundColor: theme.fn.variant({
        variant: "light",
        color: theme.primaryColor,
      }).background,
      color:
        theme.colorScheme === "dark"
          ? "white"
          : theme.colorScheme === "darkest"
          ? "black"
          : "var(--button-primary-background)",
      [`& .${"icon"}`]: {
        color:
          theme.colorScheme === "dark"
            ? "white"
            : theme.colorScheme === "darkest"
            ? "black"
            : "var(--button-primary-background)",
      },
    },
  },

  footer: {
    borderTop: `0.1rem solid ${
      theme.colorScheme === "dark" || theme.colorScheme === "darkest"
        ? theme.colors.dark[4]
        : theme.colors.gray[3]
    }`,
    paddingTop: theme.spacing.md,
  },

  chooseActivePerson: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    justifyContent: "space-between",
  },

  modalFooter: {
    display: "flex",
    justifyContent: "end",
    marginTop: "1rem",
  },
  closeButton: {
    color:
      theme.colorScheme === "dark" || theme.colorScheme === "darkest"
        ? theme.colors.dark[2]
        : "black",
  },
  mobile: {
    fontSize: "1rem",
  },
  desktop: {
    fontSize: "14px",
  },
}));

const SideBarNav = ({ userManager, drawerOpened, drawerClose }) => {
  const context = useContext(AccountContext);
  const { state } = context;
  const language = useSelector((state) => state.language);
  const { classes, cx } = useStyles();
  const [active, setActive] = useState("/");
  const [changePerson, setChangePerson] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { accountData } = state;
  const { config, isSelf } = context.state;
  const hasSubscription =
    state?.accountData?.member?.subscriptionHistory.length > 0;
  const availableLinks = getLinks(isSelf, config, hasSubscription);
  const path = window.location.pathname.split("/").pop();

  const tabs = {
    account: [
      {
        name: "home",
        link: `${process.env.PUBLIC_URL}/`,
        label: language.Pages && language.Nav["Home"],
        icon: IconHome,
      },
      {
        name: "subscription",
        link: `${process.env.PUBLIC_URL}/subscription`,
        label: language.Pages && language.Pages["subscription"],
        icon: IconRefresh,
        isSubscription: true,
      },
      {
        name: "sale history",
        link: `${process.env.PUBLIC_URL}/sale-history`,
        label: language.Pages && language.Pages["sale history"],
        icon: IconReceipt,
      },
      {
        name: "photo",
        link: `${process.env.PUBLIC_URL}/photo`,
        label: language.Pages && language.Pages["photo"],
        icon: IconCamera,
      },
      {
        name: "customer info",
        link: `${process.env.PUBLIC_URL}/customer-info`,
        label: language.Pages && language.Pages["customer info"],
        icon: IconUserEdit,
      },
      {
        name: "password",
        link: `${process.env.PUBLIC_URL}/password`,
        label: language.Pages && language.Pages["password"],
        icon: IconLock,
      },
      {
        name: "register ticket",
        link: `${process.env.PUBLIC_URL}/register-ticket`,
        label: language.Pages && language.Pages["register ticket"],
        icon: IconTicket,
      },
      {
        name: "register managed",
        link: `${process.env.PUBLIC_URL}/register-managed`,
        label: language.Pages && language.Pages["register managed"],
        icon: IconUserPlus,
      },
    ],
  };

  /* eslint-disable */
  const links = useCallback(() => {
    return tabs["account"].map((item) => {
      const [checkIfValid] = availableLinks.filter(
        (link) => item.name === link.name || item.name === "home"
      );
      if (checkIfValid && item.link) {
        return (
          <Link
            className={cx(
              classes.link,
              {
                [classes.linkActive]: item.link === active,
              },
              isMobile ? classes.mobile : classes.desktop
            )}
            key={item.link}
            onClick={() => {
              setActive(item.link);
              if (isMobile && drawerOpened) drawerClose();
            }}
            to={`${item.link}`}
          >
            <item.icon className={classes.linkIcon} stroke={1.5} />
            <span>{item.label}</span>
          </Link>
        );
      } else {
        return null;
      }
    });
  }, [availableLinks]);
  /* eslint-enable */

  useEffect(() => {
    if (path.length === 0) setActive(`${process.env.PUBLIC_URL}/`);
    else if (path.length > 0) setActive(`${process.env.PUBLIC_URL}/${path}`);
  }, [path]);

  if (!accountData) {
    return <div />;
  }
  const details = accountData.whoami.details;
  const { manages } = details;
  const isManaging = manages.length > 0;

  return (
    <>
      <Navbar
        height={800}
        width={{ sm: 250 }}
        p="md"
        className={classes.navbar}
        classNames={{ root: classes.root }}
      >
        <Navbar.Section>
          <div
            aria-live="assertive"
            role="alert"
            aria-labelledby="active-user-label"
          >
            <div
              id="active-user-label"
              style={{ opacity: 0, position: "absolute", width: 0, height: 0 }}
            >
              {language.ActiveUser} {state.activePerson.name}
            </div>
            <ActivePerson data={state.activePerson} language={language} />
          </div>
        </Navbar.Section>

        <Navbar.Section grow mt="xl">
          {links()}
        </Navbar.Section>

        <Navbar.Section className={classes.footer}>
          {isManaging && (
            <UnstyledButton
              className={`${classes.link} ${
                isMobile ? classes.mobile : classes.desktop
              }`}
              onClick={() => setChangePerson(true)}
            >
              <IconSwitchHorizontal className={classes.linkIcon} stroke={1.5} />
              <span>{language.Change_Person}</span>
            </UnstyledButton>
          )}
          <UnstyledButton
            className={`${classes.link} ${
              isMobile ? classes.mobile : classes.desktop
            }`}
            onClick={open}
          >
            <IconLogout className={classes.linkIcon} stroke={1.5} />
            <span>{language.Logout}</span>
          </UnstyledButton>
        </Navbar.Section>
      </Navbar>
      <Modal
        opened={changePerson}
        onClose={() => setChangePerson(false)}
        title={"Välj person"}
        centered
        size="md"
        closeButtonLabel={language.Close}
        classNames={{ close: classes.closeButton }}
      >
        <div className={classes.chooseActivePerson}>
          <span>Välj aktiv person</span>
          <div>
            <Person
              data={details}
              closeDialog={() => setChangePerson(false)}
              setActive={setActive}
            />
            {manages.map((p, i) => (
              <Person
                key={i}
                data={p}
                closeDialog={() => setChangePerson(false)}
                setActive={setActive}
              />
            ))}
          </div>
        </div>
        <div className={classes.modalFooter}>
          <Button
            className="primaryButton"
            onClick={() => setChangePerson(false)}
          >
            {language.Abort}
          </Button>
        </div>
      </Modal>
      <LogoutModal opened={opened} close={close} userManager={userManager} />
    </>
  );
};

export default SideBarNav;
